// Shared validation schemas for TVIA AI
import { z } from 'zod';
export const userSchema = z.object({
    id: z.string(),
    email: z.string().email(),
    name: z.string().min(1),
    createdAt: z.date(),
    updatedAt: z.date()
});
export const apiResponseSchema = z.object({
    success: z.boolean(),
    data: z.any().optional(),
    error: z.string().optional(),
    message: z.string().optional()
});
// Lawsuit validation schemas
const lawsuitCategorySchema = z.enum([
    'consumer_rights',
    'employment',
    'personal_injury',
    'contract_dispute',
    'property',
    'family',
    'criminal',
    'other'
]);
const supportedCountrySchema = z.enum(['US', 'UK', 'IL', 'AE', 'DE', 'FR']);
const lawsuitUserInfoSchema = z.object({
    firstName: z.string().min(1),
    lastName: z.string().min(1),
    email: z.string().email(),
    phone: z.string().optional(),
    address: z.string().optional(),
    country: supportedCountrySchema,
    dateOfBirth: z.string().optional(),
    occupation: z.string().optional(),
});
export const createLawsuitSchema = z.object({
    title: z.string().min(1).max(200).optional(), // AI-generated
    category: lawsuitCategorySchema.optional(), // AI-generated
    country: supportedCountrySchema,
    userStory: z.string().min(10).max(5000),
    isClassAction: z.boolean().optional().default(false),
    userInfo: lawsuitUserInfoSchema.optional()
});
export const updateLawsuitSchema = z.object({
    title: z.string().min(1).max(200).optional(),
    userStory: z.string().min(10).max(5000).optional(),
    isClassAction: z.boolean().optional(),
    userInfo: lawsuitUserInfoSchema.optional(),
    aiAnalysis: z.object({
        summary: z.string().optional(),
        estimatedTimeline: z.union([z.string(), z.date()]).transform((val) => {
            if (typeof val === 'string') {
                return new Date(val);
            }
            return val;
        }).optional(),
        accusedParty: z.object({
            name: z.string(),
            id: z.string().optional(),
            address: z.string().optional(),
            contact: z.string().optional(),
            details: z.string(),
            isBusiness: z.boolean().optional()
        }).optional(),
        estimatedClaimAmount: z.object({
            amount: z.number(),
            currency: z.string(),
            justification: z.string(),
            breakdown: z.array(z.object({
                label: z.string(),
                amount: z.number()
            }))
        }).optional()
    }).optional(),
    // Follow-up questions moved from aiAnalysis to lawsuit level
    followUpQuestions: z.array(z.object({
        id: z.string(),
        question: z.string(),
        type: z.enum(['text', 'multiple_choice', 'date', 'number', 'file_upload', 'document', 'boolean', 'select']),
        options: z.array(z.string()).optional(),
        required: z.boolean(),
        legalPurpose: z.string().optional(),
        evidenceType: z.string().optional(),
        countrySpecific: z.string().optional(),
        allowDescription: z.boolean().optional(),
        analysisDate: z.union([z.string(), z.date()]).transform((val) => {
            if (typeof val === 'string') {
                return new Date(val);
            }
            return val;
        }).optional(),
        aiModel: z.string().optional(),
        // Answer fields (merged from AIResponse)
        answer: z.any().optional(),
        language: z.enum(['en', 'he', 'ar', 'de', 'fr', 'es', 'it', 'pt', 'ru', 'zh', 'ja', 'ko']).optional(),
        files: z.array(z.any()).optional(),
        answeredAt: z.union([z.string(), z.date()]).transform((val) => {
            if (typeof val === 'string') {
                return new Date(val);
            }
            return val;
        }).optional()
    })).optional(),
    // Similar cases moved from aiAnalysis to lawsuit level
    similarCases: z.array(z.object({
        title: z.string().optional(),
        description: z.string().optional(),
        outcome: z.string().optional(),
        relevance: z.string().optional(),
        jurisdiction: z.string().optional(),
        settlementAmount: z.union([z.number(), z.string()]).optional(),
        keyFactors: z.string().optional(),
        reference: z.string().optional()
    })).optional()
});
export const joinLawsuitSchema = z.object({
    lawsuitId: z.string()
});
export const searchLawsuitsSchema = z.object({
    page: z.coerce.number().min(1).default(1),
    limit: z.coerce.number().min(1).max(100).default(10),
    category: lawsuitCategorySchema.optional(),
    country: supportedCountrySchema.optional(),
    status: z.string().optional(),
    isClassAction: z.boolean().optional(),
    search: z.string().optional()
});
export const aiResponsesSchema = z.object({
    lawsuitId: z.string(),
    answers: z.record(z.string(), z.any()),
    language: z.string().optional()
});
// Additional validation schemas
export const registerSchema = z.object({
    email: z.string().email(),
    password: z.string().min(8),
    name: z.string().min(1),
    firstName: z.string().optional(),
    lastName: z.string().optional()
});
export const loginSchema = z.object({
    email: z.string().email(),
    password: z.string()
});
export const updateUserSchema = z.object({
    name: z.string().min(1).optional(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    phone: z.string().optional(),
    idNumber: z.string().optional(),
    address: z.string().optional(),
    country: z.string().optional(),
    bio: z.string().optional()
});
export const languagePreferenceSchema = z.object({
    preferredLanguage: z.enum(['en', 'he', 'ar', 'de', 'fr', 'es', 'it', 'pt', 'ru', 'zh', 'ja', 'ko'])
});
export const createPaymentIntentSchema = z.object({
    lawsuitId: z.string()
});
export const confirmPaymentSchema = z.object({
    paymentIntentId: z.string()
});
export const updateLawsuitStatusSchema = z.object({
    status: z.enum(['draft', 'analyzing', 'questions_ready', 'analysis_complete', 'pending_payment', 'submitted', 'in_progress', 'settled', 'won', 'lost', 'dismissed']),
    notes: z.string().optional()
});
// JSON Schema for AIAnalysis (single source of truth, Gemini/JSON Schema compatible)
export function getAIAnalysisJsonSchema() {
    return {
        $schema: "https://json-schema.org/draft/2020-12/schema",
        title: "AIAnalysis",
        description: "Schema for AI-generated lawsuit analysis results.",
        type: "object",
        properties: {
            caseTitle: { type: "string", description: "Generated case title." },
            accusedParty: {
                type: "object",
                properties: {
                    name: { type: "string", description: "Name of the main defendant or organization being sued." },
                    id: { type: "string", description: "Registration number, personal ID, or other identifier if available." },
                    address: { type: "string", description: "Address of the accused party (required if available)." },
                    contact: { type: "string", description: "Contact information (email, phone) if available." },
                    details: { type: "string", description: "Short description of the accused party (e.g., company, individual, government, etc.)." },
                    isBusiness: { type: "boolean", description: "Whether the accused party is a business/organization (true) or an individual person (false)." }
                },
                required: ["name", "details"],
                additionalProperties: false
            },
            summary: { type: "string", description: "A concise, professional summary of the case." },
            detailedSummary: { type: "string", description: "Comprehensive, detailed review of what happened including chronological sequence of events, all parties involved, specific damages/injuries, legal implications, dates, amounts, locations, and factual details needed for legal filings." },
            category: { type: "string", description: "Determined legal category." },
            totalCost: { type: "number", description: "Total cost estimate." },
            currency: { type: "string", description: "Currency code." },
            estimatedClaimAmount: {
                type: "object",
                properties: {
                    amount: { type: "number" },
                    currency: { type: "string" },
                    justification: { type: "string" },
                    breakdown: {
                        type: "array",
                        items: {
                            type: "object",
                            properties: {
                                label: { type: "string" },
                                amount: { type: "number" }
                            },
                            required: ["label", "amount"],
                            additionalProperties: false
                        }
                    }
                },
                required: ["amount", "currency", "justification", "breakdown"],
                additionalProperties: false
            },
            legalBasis: {
                type: "array",
                items: { type: "string" },
                description: "Relevant laws, statutes, and regulations."
            },
            strengthScore: { type: "number", description: "Strength score (0-100)." },
            confidence: { type: "number", description: "Confidence level (0-1)." },
            countrySpecificFactors: {
                type: "array",
                items: { type: "string" },
                description: "Important legal considerations specific to the country."
            },
            estimatedTimeline: { type: "string", format: "date", description: "Expected resolution date for the case (ISO 8601, YYYY-MM-DD)." },
            jurisdictionNotes: { type: "string", description: "Important procedural or substantive law considerations." },
            classActionViability: {
                type: "object",
                properties: {
                    recommended: { type: "boolean" },
                    reason: { type: "string" },
                    potentialParticipants: { type: "string" }
                },
                required: ["recommended", "reason", "potentialParticipants"],
                additionalProperties: false
            },
            analysisDate: { type: "string", description: "Date of analysis (ISO 8601)." },
            aiModel: { type: "string", description: "AI model used." },
            generatedDocuments: {
                type: "object",
                patternProperties: {
                    "^[a-z]{2}$": { type: "string" }
                },
                additionalProperties: false,
                description: "Generated documents by language code."
            }
        },
        required: [
            "caseTitle", "accusedParty", "summary", "detailedSummary", "category", "totalCost", "currency",
            "estimatedClaimAmount", "legalBasis", "strengthScore", "confidence",
            "countrySpecificFactors", "estimatedTimeline", "jurisdictionNotes",
            "classActionViability", "analysisDate", "aiModel"
        ],
        additionalProperties: false
    };
}
// Validation functions
export function validateCreateLawsuit(data) {
    return createLawsuitSchema.parse(data);
}
export function validateUpdateLawsuit(data) {
    return updateLawsuitSchema.parse(data);
}
export function validateJoinLawsuit(data) {
    return joinLawsuitSchema.parse(data);
}
export function validateSearchLawsuits(data) {
    return searchLawsuitsSchema.parse(data);
}
export function validateAIResponses(data) {
    return aiResponsesSchema.parse(data);
}
export function validateRegister(data) {
    return registerSchema.parse(data);
}
export function validateLogin(data) {
    return loginSchema.parse(data);
}
export function validateUpdateUser(data) {
    return updateUserSchema.parse(data);
}
export function validateLanguagePreference(data) {
    return languagePreferenceSchema.parse(data);
}
export function validateCreatePaymentIntent(data) {
    return createPaymentIntentSchema.parse(data);
}
export function validateConfirmPayment(data) {
    return confirmPaymentSchema.parse(data);
}
export function validateUpdateLawsuitStatus(data) {
    return updateLawsuitStatusSchema.parse(data);
}
